import React from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  IconButton,
  Grid,
  useTheme,
  InputAdornment
} from "@mui/material";
import {
  Trash2,
  Plus,
  CreditCard,
  DollarSign,
  Info
} from 'lucide-react';
import Constants from "../../utils/constants/Constants";
import { tokens } from "../../theme";

const PaymentError = ({ errors, index, field }) => {
  const message = errors.payments?.[index]?.[field]?.message;

  if (!message) {
    return null;
  }

  return (
    <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
      {message}
    </Typography>
  );
};

const MultiPaymentForm = ({
  addPayment,
  amountChangeHandler,
  control,
  Controller,
  errors,
  fields,
  register,
  methods,
  removePayment,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const PAYMENT_METHODS = Object.values(Constants.PAYMENT_METHODS).sort((a, b) =>
    a.localeCompare(b)
  );
  const otherIndex = PAYMENT_METHODS.indexOf(Constants.PAYMENT_METHODS.other);
  if (otherIndex > -1) {
    PAYMENT_METHODS.splice(otherIndex, 1);
  }

  return (
    <Box sx={{ width: '100%' }}>
      {fields.map((field, index) => (
        <Box
          key={field.id}
          sx={{
            p: 3,
            mb: 2,
            borderRadius: "16px",
            border: `1px solid ${theme.palette.mode === "dark" ? colors.primary[300] : colors.grey[200]}`,
            bgcolor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.01)",
            position: 'relative'
          }}
        >
          {fields.length > 1 && (
            <IconButton
              onClick={() => removePayment(index)}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                color: colors.redAccent[500],
                "&:hover": { bgcolor: `${colors.redAccent[500]}1A` }
              }}
              size="small"
            >
              <Trash2 size={18} />
            </IconButton>
          )}

          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={12} md={5}>
              <FormControl fullWidth variant="outlined" error={!!errors.payments?.[index]?.method}>
                <InputLabel id={`payment-method-label-${index}`} sx={{ color: colors.grey[300], "&.Mui-focused": { color: colors.greenAccent[500] } }}>Payment Method</InputLabel>
                <Controller
                  name={`payments[${index}].method`}
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <Select
                      labelId={`payment-method-label-${index}`}
                      {...field}
                      value={field.value || ""}
                      label="Payment Method"
                      sx={{
                        borderRadius: "12px",
                        color: colors.grey[100],
                        "& .MuiOutlinedInput-notchedOutline": { borderColor: colors.grey[400] },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: colors.greenAccent[500] },
                        "& .MuiSvgIcon-root": { color: colors.grey[100] }
                      }}
                    >
                      <MenuItem value="" sx={{ color: colors.grey[100] }}>Select method</MenuItem>
                      {PAYMENT_METHODS.map((method) => (
                        <MenuItem key={method} value={method} sx={{ color: colors.grey[100] }}>{method}</MenuItem>
                      ))}
                      <MenuItem value={Constants.PAYMENT_METHODS.other} sx={{ color: colors.grey[100] }}>Other</MenuItem>
                    </Select>
                  )}
                />
                <PaymentError errors={errors} index={index} field="method" />
              </FormControl>
            </Grid>

            <Grid item xs={12} md={7}>
              <Controller
                name={`payments[${index}].amount`}
                control={control}
                render={({ field: { onChange, value } }) => (
                  <TextField
                    fullWidth
                    label="Transaction Amount"
                    type="text"
                    value={value ?? ""}
                    onChange={(e) => amountChangeHandler(onChange)(e, index)}
                    error={!!errors.payments?.[index]?.amount}
                    variant="outlined"
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
                    inputProps={{ inputMode: "decimal" }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <DollarSign size={18} color={colors.grey[400]} />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
              <PaymentError errors={errors} index={index} field="amount" />
            </Grid>

            {methods[index].method === Constants.PAYMENT_METHODS.card && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Card Reference (Last 4 Digits)"
                  {...register(`payments[${index}].cardLastFour`)}
                  placeholder="e.g., 4242"
                  inputProps={{ maxLength: 4 }}
                  error={!!errors.payments?.[index]?.cardLastFour}
                  variant="outlined"
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CreditCard size={18} color={colors.grey[400]} />
                      </InputAdornment>
                    ),
                  }}
                />
                <PaymentError errors={errors} index={index} field="cardLastFour" />
              </Grid>
            )}

            {methods[index].method === Constants.PAYMENT_METHODS.other && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Specify Payment Method"
                  {...register(`payments[${index}].otherDetails`)}
                  placeholder="Ref. number or description"
                  error={!!errors.payments?.[index]?.otherDetails}
                  variant="outlined"
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Info size={18} color={colors.grey[400]} />
                      </InputAdornment>
                    ),
                  }}
                />
                <PaymentError errors={errors} index={index} field="otherDetails" />
              </Grid>
            )}
          </Grid>
        </Box>
      ))}

      <Button
        fullWidth
        variant="outlined"
        startIcon={<Plus size={18} />}
        onClick={addPayment}
        sx={{
          py: 1.5,
          borderRadius: "12px",
          textTransform: "none",
          fontWeight: "600",
          borderStyle: 'dashed',
          borderColor: colors.grey[400],
          color: colors.grey[100],
          "&:hover": { borderColor: colors.greenAccent[500], bgcolor: `${colors.greenAccent[500]}0D` }
        }}
      >
        Append Payment Channel
      </Button>
    </Box>
  );
};

MultiPaymentForm.propTypes = {
  addPayment: PropTypes.func.isRequired,
  amountChangeHandler: PropTypes.func.isRequired,
  control: PropTypes.object.isRequired,
  Controller: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
  fields: PropTypes.array.isRequired,
  methods: PropTypes.array.isRequired,
  register: PropTypes.func.isRequired,
  removePayment: PropTypes.func.isRequired,
};

PaymentError.propTypes = {
  errors: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  field: PropTypes.string.isRequired,
};

export default MultiPaymentForm;
